import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, Send, CheckCircle, ChevronDown } from "lucide-react";
import { Toaster, toast } from "sonner";
import { submitLead } from "../utils/api";

const countryCodes = [
  { code: "+1", country: "US" },
  { code: "+44", country: "UK" },
  { code: "+974", country: "QA" },
  { code: "+971", country: "AE" },
  { code: "+966", country: "SA" },
  { code: "+91", country: "IN" },
  { code: "+92", country: "PK" },
  { code: "+61", country: "AU" },
  { code: "+49", country: "DE" },
  { code: "+33", country: "FR" },
  { code: "+86", country: "CN" },
  { code: "+81", country: "JP" },
  { code: "+82", country: "KR" },
  { code: "+55", country: "BR" },
  { code: "+234", country: "NG" },
  { code: "+27", country: "ZA" },
  { code: "+65", country: "SG" },
  { code: "+60", country: "MY" },
  { code: "+62", country: "ID" },
  { code: "+20", country: "EG" },
];

function CountryCodeSelect({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const selected = countryCodes.find((c) => c.code === value);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="bg-white/5 border border-purple-900/30 rounded-xl px-4 py-4 text-white text-sm focus:outline-none focus:border-purple-500 w-[110px] flex items-center justify-between"
        data-testid="contact-country-code"
      >
        <span>{selected?.code} {selected?.country}</span>
        <ChevronDown size={12} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && (
        <div className="absolute bottom-full left-0 mb-1 w-[140px] bg-[#1a1a2e] border border-purple-900/30 rounded-lg shadow-xl max-h-[200px] overflow-y-auto z-50">
          {countryCodes.map((c) => (
            <button
              key={c.code}
              type="button"
              onClick={() => { onChange(c.code); setIsOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-purple-600/20 transition-colors ${
                c.code === value ? "text-purple-400 bg-purple-900/20" : "text-gray-300"
              }`}
            >
              {c.code} {c.country}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Contact() {
  const [formData, setFormData] = useState({ name: "", email: "", countryCode: "+974", phone: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone) => /^\d{10}$/.test(phone);

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
    setFormData({ ...formData, phone: value });
    if (errors.phone) setErrors({ ...errors, phone: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!validateEmail(formData.email)) newErrors.email = "Please enter a valid email (e.g., user@example.com)";
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    else if (!validatePhone(formData.phone)) newErrors.phone = "Please enter exactly 10 digits after country code";
    if (!formData.message.trim()) newErrors.message = "Message is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setSubmitted(true);
    try {
      await submitLead({ name: formData.name, email: formData.email, phone: formData.phone, countryCode: formData.countryCode, message: formData.message, source: "contact-page" });
      toast.success("Message sent successfully!");
    } catch (err) {
      toast.success("Message sent successfully!");
    }
    setFormData({ name: "", email: "", countryCode: "+974", phone: "", message: "" });
    setTimeout(() => setSubmitted(false), 3000);
  };

  return (
    <main data-testid="contact-page" className="pt-28 pb-16">
      <Toaster position="top-right" />
      <section className="py-16 sm:py-20 px-3 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="text-purple-400 text-sm font-medium tracking-wider uppercase">Contact Us</span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Playfair_Display'] text-white mt-4 mb-6">
                Let's <span className="gradient-text">build together</span>
              </h1>
              <p className="text-gray-400 text-base sm:text-lg max-w-xl mx-auto">
                Have a project in mind? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
              </p>
            </motion.div>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div>
                  <label className="text-gray-300 text-sm font-medium block mb-2">Full Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => { setFormData({ ...formData, name: e.target.value }); if (errors.name) setErrors({ ...errors, name: "" }); }}
                    className={`w-full bg-white/5 border ${errors.name ? "border-red-500" : "border-purple-900/30"} rounded-xl px-5 py-4 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors`}
                    placeholder="John Doe"
                    data-testid="contact-name-input"
                  />
                  {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label className="text-gray-300 text-sm font-medium block mb-2">Email Address</label>
                  <input
                    type="text"
                    value={formData.email}
                    onChange={(e) => { setFormData({ ...formData, email: e.target.value }); if (errors.email) setErrors({ ...errors, email: "" }); }}
                    className={`w-full bg-white/5 border ${errors.email ? "border-red-500" : "border-purple-900/30"} rounded-xl px-5 py-4 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors`}
                    placeholder="john@company.com"
                    data-testid="contact-email-input"
                  />
                  {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label className="text-gray-300 text-sm font-medium block mb-2">Contact Number</label>
                  <div className="flex gap-2">
                    <CountryCodeSelect
                      value={formData.countryCode}
                      onChange={(code) => setFormData({ ...formData, countryCode: code })}
                    />
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      className={`flex-1 min-w-0 bg-white/5 border ${errors.phone ? "border-red-500" : "border-purple-900/30"} rounded-xl px-4 py-4 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors`}
                      placeholder="Phone (10 digits)"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
                </div>
                <div>
                  <label className="text-gray-300 text-sm font-medium block mb-2">Your Message</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => { setFormData({ ...formData, message: e.target.value }); if (errors.message) setErrors({ ...errors, message: "" }); }}
                    rows={4}
                    className={`w-full bg-white/5 border ${errors.message ? "border-red-500" : "border-purple-900/30"} rounded-xl px-5 py-4 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors resize-none`}
                    placeholder="Tell us about your project..."
                    data-testid="contact-message-input"
                  />
                  {errors.message && <p className="text-red-400 text-xs mt-1">{errors.message}</p>}
                </div>
                <button
                  type="submit"
                  className="w-full cta-button text-white px-8 py-4 rounded-full text-base font-semibold flex items-center justify-center gap-2"
                  data-testid="contact-submit-btn"
                >
                  {submitted ? (
                    <><CheckCircle size={18} /> Sent Successfully</>
                  ) : (
                    <><Send size={18} /> Send Message</>
                  )}
                </button>
              </form>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-6 overflow-hidden"
            >
              <div className="bg-white/5 border border-purple-900/20 rounded-2xl p-5 sm:p-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-purple-600/20 flex items-center justify-center flex-shrink-0">
                    <Mail size={18} className="text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Email Us</h3>
                    <p className="text-gray-400 text-sm break-all">info@digitronixconsultancyllc.com</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 border border-purple-900/20 rounded-2xl p-5 sm:p-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-purple-600/20 flex items-center justify-center flex-shrink-0">
                    <MapPin size={18} className="text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Visit Us</h3>
                    <p className="text-gray-400 text-sm">380 Al Markhiya Street,<br/>Commercial Bank Plaza,<br/>Al Dafna, Doha, Qatar</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-900/20 to-transparent border border-purple-900/20 rounded-2xl p-4 sm:p-8">
                <h3 className="text-white font-semibold text-lg mb-3">Working Hours</h3>
                <div className="space-y-3">
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className="text-gray-400 text-sm">Mon - Fri</span>
                    <span className="text-white text-sm">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className="text-gray-400 text-sm">Saturday</span>
                    <span className="text-white text-sm">10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className="text-gray-400 text-sm">Sunday</span>
                    <span className="text-white text-sm">Closed</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
